import { computed, inject } from '@angular/core';

import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';

import { CustomerType } from '../../../../definitions/enums/customers/customer-type.enum';
import { BaseCustomerConfig } from '../../../../definitions/interfaces/customer/customer-config';
import { CustomerService } from '../../../../services/customer/customer.service';
import { UserConfigsStoreFacade } from '../../../../stores/global-store/user-configs/user-configs-store.facade';
import { CommonMethodsService } from '../../../../utils/common-methods/common-methods.service';

interface SwitchViewSidebarState {
  loading: boolean;
  filterValue: string;
  allCustomers: Array<BaseCustomerConfig>;
  selectedCustomer: BaseCustomerConfig | undefined;
}

const initialState: SwitchViewSidebarState = {
  loading: false,
  filterValue: '',
  allCustomers: [],
  selectedCustomer: undefined
};

export const SwitchExternalUserStore = signalStore(
  withState<SwitchViewSidebarState>(initialState),
  withComputed(store => {
    return {
      getFilteredCustomers: computed(() => {
        const filterValue = store.filterValue().toLowerCase();
        const allCustomers = store.allCustomers();

        return allCustomers.filter((customer: BaseCustomerConfig) =>
          customer.name.toLowerCase().includes(filterValue)
        );
      }),
      allCustomersCount: computed(() => store.allCustomers()?.length ?? 0)
    };
  }),
  withMethods(store => {
    const customerService = inject(CustomerService);
    const userConfigsFacade = inject(UserConfigsStoreFacade);

    const adjustCustomersList = (customers: BaseCustomerConfig[]): BaseCustomerConfig[] => {
      return customers.filter(customer =>
        userConfigsFacade.allReplenishmentCustomersSignal().includes(customer.erpRef));
    };

    const loadCustomersByCountryCode = rxMethod<string>(
      pipe(
        tap(() => patchState(store, { loading: true })),
        switchMap((countryCode: string) => {
          const filter = CommonMethodsService.getFiltersBase64String({ customerType: [ CustomerType.replenishment, CustomerType.hybrid ]});
          return customerService.getCustomersByCountryCode(countryCode, { id: 'name', start: 'asc', disableClear: true }, filter)
            .pipe(tapResponse({
              next: (customers: Array<BaseCustomerConfig>) => {
                const adjustedCustomers = adjustCustomersList(customers);
                patchState(
                  store,
                  { allCustomers: adjustedCustomers, loading: false, selectedCustomer: adjustedCustomers[0], filterValue: '' }
                );
              },
              error: () => patchState(store, { allCustomers: [], loading: false, selectedCustomer: undefined, filterValue: '' })
            }));
        })
      )
    );
    return {
      loadCustomersByCountryCode
    };
  }),
  withMethods(store => {
    const setCustomerFilter = (value: string | undefined): void => {
      patchState(store, { filterValue: value ?? '' });
    };

    return {
      setCustomerFilter
    };
  })
);
